<template>
  <el-dropdown
    trigger="click"
    placement="bottom-end"
    class="user-dropdown"
    @visible-change="handleUserVisible"
    :class="{ 'is-active': userVisible }"
    v-if="user.uid"
  >
    <div class="user-section">
      <div class="user-icon">
        <gravatar v-if="provider.gravatar_on" :email="userEmail" :size="32" />
        <img
          v-else
          src="/img/avatar.jpg"
          alt="Avatar"
          :style="{ width: `32px`, height: `32px` }"
        />
      </div>
      <div class="user-name">{{ user.name }}</div>
      <i
        v-if="userVisible"
        class="el-icon-arrow-up el-icon--right dropdown-icon"
      ></i>
      <i v-else class="el-icon-arrow-down el-icon--right dropdown-icon"></i>
    </div>

    <el-dropdown-menu slot="dropdown" class="user-dropdown-menu">
      <div class="dropdown-items-wrapper">
        <el-dropdown-item
          v-if="displayAdministration"
          class="user-section-separator"
        >
          <div>{{ $t('TopToolbar_Administration') }}</div>
        </el-dropdown-item>

        <user-nav-item
          v-for="item in adminItems"
          :key="item.token"
          :href="item.href"
          :icon="item.icon"
          :token="item.token"
        />

        <el-dropdown-item class="user-section-separator">
          <div>{{ $t('TopToolbar_AccountInformation') }}</div>
        </el-dropdown-item>

        <user-nav-item
          v-for="item in userItems"
          :key="item.token"
          :href="item.href"
          :icon="item.icon"
          :token="item.token"
          :element-icon="item.elementIcon"
          :blank="item.blank"
        />
      </div>
    </el-dropdown-menu>
  </el-dropdown>
</template>

<script>
import UserNavItem from '@/common/components/navigation/TopToolbarUserNavItem'

export default {
  name: 'TopToolbarUserDropdown',

  components: {
    UserNavItem
  },

  data() {
    return {
      userVisible: false
    }
  },

  computed: {
    adminItems() {
      // only include items that pass the isAllowed check
      return [
        {
          href: '/user/provider/index',
          icon: 'provider-info',
          token: 'TopToolbar_Providers',
          isAllowed:
            this.$isRole('admin') ||
            this.$isRole('admin-support') ||
            this.$isRole('admin-viewer')
        },
        {
          href: '/user/company/index',
          icon: 'companies',
          token: 'TopToolbar_Companies',
          isAllowed: this.isAdmin
        },
        {
          href: '/user/project/index',
          icon: 'folder-opened',
          token: 'TopToolbar_Projects',
          isAllowed: this.isAdmin
        },
        {
          href: '/user/application/index',
          icon: 'application',
          token: 'TopToolbar_Applications',
          isAllowed: this.isAdmin
        },
        {
          href: '/user/user/index',
          icon: 'user-management',
          token: 'TopToolbar_UsersManagement',
          isAllowed:
            ['company-admin', 'company-manager'].includes(this.user.role) ||
            this.isAdmin
        },
        {
          href: `/user/provider/update?id=${this.provider.id}`,
          icon: 'provider-info',
          token: 'TopToolbar_ProviderInfo',
          isAllowed: this.user.role === 'provider-admin'
        },
        {
          href: `/user/company/view?id=${this.company.id}`,
          icon: 'companies',
          token: 'TopToolbar_CompanyProfile',
          isAllowed:
            this.company &&
            ['company-admin', 'company-manager'].includes(this.user.role)
        },
        {
          href: '/email/email-address/index',
          icon: 'email-symbol',
          token: 'TopToolbar_EmailAddresses',
          isAllowed: this.isAdmin || this.user.role === 'company-admin'
        },
        {
          href: '/payment/account/company-statistics',
          icon: 'toolbar-finances',
          token: 'TopToolbar_BillingInfo',
          isAllowed:
            this.isAdmin ||
            this.$isRole('company-admin') ||
            this.$isRole('company-manager')
        },
        {
          href: '/page/page/index',
          icon: 'pages',
          token: 'TopToolbar_Pages',
          isAllowed: this.isAdmin
        },
        {
          href: '/translation/source',
          icon: 'translations',
          token: 'TopToolbar_Translations',
          isAllowed: this.isAdmin
        },
        {
          href: '/integration/integration/index',
          icon: 'integrations-nav',
          token: 'TopToolbar_Integrations',
          isAllowed:
            this.$isRole('admin') ||
            this.$isRole('admin-viewer') ||
            this.$isRole('admin-support')
        },
        {
          href: '/applog/request/mobile-api',
          icon: 'toolbar-logs',
          token: 'TopToolbar_Logs',
          isAllowed:
            this.$isRole('admin') ||
            this.$isRole('admin-viewer') ||
            this.$isRole('admin-support')
        }
      ].filter(item => item.isAllowed)
    },

    userItems() {
      // only include items that pass the isAllowed check
      return [
        {
          href: '/user/user/update-profile',
          icon: 'person',
          token: 'TopToolbar_AccountInfo',
          isAllowed: true
        },
        {
          href: this.findPage('/demo').url,
          icon: 'desktop-screen',
          token: 'TopToolbar_Demo',
          isAllowed: this.findPage('/demo')
        },
        {
          href: this.findPage('/about').url,
          icon: 'text',
          token: 'TopToolbar_About',
          isAllowed: this.findPage('/about')
        },
        {
          href: 'https://docs.xtremepush.com',
          icon: 'documents',
          token: 'TopToolbar_Docs',
          isAllowed: true,
          blank: true
        },
        {
          href: '/user/auth/account-picker',
          icon: '',
          elementIcon: 'el-icon-more',
          token: 'TopToolbar_SwitchAccount',
          isAllowed: this.user.ssologin
        },
        {
          href: '/help',
          icon: '',
          elementIcon: 'el-icon-question',
          token: 'TopToolbar_ContactSupport',
          isAllowed: true
        },
        {
          href: '/logout',
          icon: 'logout',
          token: 'TopToolbar_Logout',
          isAllowed: true
        }
      ].filter(item => item.isAllowed)
    },

    isAdmin() {
      return [
        'admin',
        'admin-support',
        'admin-viewer',
        'provider-admin',
        'provider-admin-viewer'
      ].includes(this.user.role)
    },

    displayAdministration() {
      let requiredRoles = [
        'admin',
        'admin-support',
        'admin-viewer',
        'provider-admin',
        'provider-admin-viewer',
        'company-admin',
        'company-manager'
      ]

      return requiredRoles.includes(this.user.role)
    },

    user() {
      return this.$store.state.app.user
    },

    userEmail() {
      return this.$store.state.app.user.email || ''
    },

    provider() {
      return this.$store.state.app.provider
    },

    company() {
      return this.$store.state.app.company || false
    }
  },

  methods: {
    findPage(pageUrl) {
      return (
        this.$store.state.app.pages.find(item => item.url === pageUrl) || false
      )
    },

    handleUserVisible(val) {
      this.userVisible = val
    }
  }
}
</script>
<style scoped>
.user-dropdown {
  display: flex;
  background: #1d87c8;
  cursor: pointer;
  border-left: 1px solid #176ca0;
}

.user-dropdown:hover {
  background: #0775b6;
}

.user-dropdown.is-active {
  background: #0775b6;
}

.user-section {
  margin-left: auto;
  padding-left: 37px;
  display: flex;
}

.user-section .dropdown-icon {
  color: #fff;
  right: 0;
  font-size: 16px;
  font-weight: 600;
  margin: auto 37px auto 13px;
}

.user-name {
  color: #fff;
  font-size: 14px;
  margin: auto;
}

.user-icon {
  margin: auto 12px auto 0;
}

.user-icon img {
  border-radius: 50%;
}

.user-dropdown-menu {
  top: 55px !important;
  right: 32px !important;
  left: auto !important;
  margin-top: 2px;
  min-width: 210px;
  max-width: 300px;
  border-radius: 4px !important;
  padding: 0;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.dropdown-items-wrapper {
  max-height: calc(100vh - 64px);
  overflow-y: scroll;
}

.user-section-separator {
  background-color: #fafafa;
  cursor: default;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  height: 48px;
  display: flex;
  align-items: center;
  padding: 0 15px;
}

.user-section-separator:hover,
.user-section-separator:focus {
  color: unset;
  background-color: #fafafa;
}
</style>



// WEBPACK FOOTER //
// src/common/components/navigation/TopToolbarUserDropdown.vue